// src/components/settings/CityAutocomplete.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Lightbulb } from 'lucide-react';
import logger from '@/shared/utils/core/logger';

/**
 * CityAutocomplete - Worldwide city search
 *
 * Uses Photon API (Komoot / OpenStreetMap) directly from the browser — free, no API key.
 * Timezone is looked up via timeapi.io when the user selects a city — also free, no key.
 * Both APIs allow browser-side CORS requests.
 */

// Fetch city suggestions from Photon (OSM-based, free, CORS-enabled)
async function fetchCitySuggestions(query) {
  const encoded = encodeURIComponent(query.trim());
  const url = `https://photon.komoot.io/api/?q=${encoded}&limit=8&lang=en`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Photon API error: ${res.status}`);
  const data = await res.json();
  return (data.features || [])
    .filter(f => {
      const t = f.properties?.type;
      // Keep place types that make sense as birth locations
      return ['city', 'town', 'village', 'hamlet', 'suburb', 'borough',
               'municipality', 'district', 'county'].includes(t);
    })
    .slice(0, 6)
    .map(f => {
      const p = f.properties || {};
      const [lon, lat] = f.geometry?.coordinates || [0, 0];
      const city = p.name || '';
      const state = p.state || p.county || '';
      const country = p.country || '';
      const countryCode = p.countrycode?.toUpperCase() || '';

      let displayName = city;
      if (state && state !== city) displayName += `, ${state}`;
      if (countryCode) displayName += `, ${countryCode}`;

      return {
        name: displayName.trim() || p.name || 'Unknown',
        fullName: [city, state, country].filter(Boolean).join(', '),
        lat: parseFloat(lat),
        lon: parseFloat(lon),
        city,
        state,
        country,
        type: p.type || 'location',
      };
    });
}

// Lookup IANA timezone for lat/lon — only called when user selects a city
async function fetchTimezone(lat, lon) {
  try {
    const res = await fetch(
      `https://timeapi.io/api/TimeZone/coordinate?latitude=${lat}&longitude=${lon}`,
      { headers: { Accept: 'application/json' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.timeZone || null;
  } catch {
    return null;
  }
}

export default function CityAutocomplete({ value, onChange, className = '' }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false); // timezone lookup in progress
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchError, setSearchError] = useState(false);

  const debounceTimer = useRef(null);
  const containerRef = useRef(null);
  const isPropUpdateRef = useRef(false);

  // Sync external value changes (e.g. profile load) — mark as non-user-typed
  useEffect(() => {
    isPropUpdateRef.current = true;
    setInputValue(value || '');
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search — only runs on user-typed input, not prop-driven updates
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // Skip search if this change came from the parent prop, not user typing
    if (isPropUpdateRef.current) {
      isPropUpdateRef.current = false;
      return;
    }

    if (!inputValue || inputValue.trim().length < 2) {
      setResults([]);
      setShowDropdown(false);
      setSearchError(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      setSearchError(false);
      try {
        const cities = await fetchCitySuggestions(inputValue);
        setResults(cities);
        setShowDropdown(true);
        setSelectedIndex(0);
      } catch (err) {
        logger.error('[CityAutocomplete] Search error:', err);
        setResults([]);
        setShowDropdown(true); // show error state in dropdown
        setSearchError(true);
      } finally {
        setLoading(false);
      }
    }, 600); // 600ms — stays within Photon's rate limit

    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [inputValue]);

  const handleSelect = async (result) => {
    setInputValue(result.name);
    setShowDropdown(false);
    setResults([]);
    setSelecting(true);

    // Lookup timezone (only on selection — keeps search fast)
    const timezone = await fetchTimezone(result.lat, result.lon);

    onChange({
      name: result.name,
      lat: result.lat,
      lon: result.lon,
      city: result.city,
      state: result.state,
      country: result.country,
      timezone,
    });
    setSelecting(false);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleFocus = () => {
    if (inputValue.length >= 2 && results.length > 0) setShowDropdown(true);
  };

  const isSpinning = loading || selecting;

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder="e.g., New York, NY"
        className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-[#ffb85e] transition-colors ${className}`}
      />

      {/* Spinner — search loading OR timezone lookup after selection */}
      {isSpinning && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-5 h-5 border-2 border-[#ffb85e] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Results dropdown */}
      {showDropdown && !loading && results.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-[#1a1410]/95 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
          {results.map((result, index) => (
            <button
              key={`${result.lat}-${result.lon}-${index}`}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(result); }} // mousedown before blur
              className={`w-full px-4 py-3 text-left transition-colors ${
                index === selectedIndex ? 'bg-[#ffb85e]/20 text-white' : 'text-white/90 hover:bg-white/10'
              } ${index === 0 ? 'rounded-t-xl' : ''} ${
                index === results.length - 1 ? 'rounded-b-xl' : 'border-b border-white/5'
              }`}
            >
              <div className="font-medium text-white text-sm">{result.name}</div>
              <div className="text-xs text-white/50 mt-0.5">
                {result.type && <span className="capitalize">{result.type}</span>}
                {result.type && ' · '}
                {result.lat.toFixed(2)}°, {result.lon.toFixed(2)}°
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {showDropdown && !loading && !searchError && results.length === 0 && inputValue.length >= 2 && (
        <div className="absolute z-50 w-full mt-2 bg-[#1a1410]/95 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl p-4 text-center">
          <div className="text-white/80 text-sm">No cities found for "{inputValue}"</div>
          <div className="text-xs text-white/50 mt-1.5">Try a different spelling or nearby city</div>
        </div>
      )}

      {/* Search error */}
      {showDropdown && !loading && searchError && (
        <div className="absolute z-50 w-full mt-2 bg-[#1a1410]/95 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl p-4 text-center">
          <div className="text-white/60 text-sm">Search unavailable — type your city manually</div>
        </div>
      )}

      {/* Help text */}
      {!showDropdown && !isSpinning && (
        <div className="mt-2 text-xs text-white/40 font-light flex items-center gap-1.5">
          <Lightbulb size={14} className="text-[#ffb85e]" />
          <span>
            {selecting
              ? 'Looking up timezone…'
              : 'Enter your birth city (e.g., New York, NY or Tokyo, Japan)'}
          </span>
        </div>
      )}
    </div>
  );
}
